import type { ReactNode } from 'react';

import { SkeletonBlock } from '@/components/common/SkeletonBlock';
import { EmptyState } from '@/components/shared/EmptyState';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatPolicyCount, formatTtl } from '@/features/common/format';
import { PERMISSION_ACTIONS, roleResource } from '@/features/permissions/model';
import { useCan } from '@/features/permissions/useCan';
import { cn } from '@/lib/utils';
import type { Role } from '../types';

/** Plain-language rationale in the §14.5 style — never a raw action string. */
const ROLE_DENIED_TOOLTIP =
  "You don't have permission to change this role. Ask an organization owner or admin.";

export interface RoleChecklistProps {
  roles: Role[];
  selectedRoleIds: string[];
  onToggle: (roleId: string, checked: boolean) => void;
  isLoading?: boolean;
  /** Row currently mid-mutation: its checkbox is replaced by a spinner (C2). */
  pendingRoleId?: string | null;
  disabled?: boolean;
  /**
   * Per-row trailing control. The Assign roles dialog passes its renew button
   * here, so both dialogs share one row component (design §15.3); it receives the
   * row's denial state so the trailing button is disabled with the checkbox.
   */
  renderTrailing?: (role: Role, denied: boolean) => ReactNode;
  /**
   * Gate each row on the server's own guard (`roles:assign` on `role:${id}`).
   * Only the Assign roles dialog sets it, because those toggles mutate row by
   * row; the Add member dialog leaves it off — there the selection is just data
   * inside `POST .../members`, which is gated by `organizations:member:add`
   * instead, and no probe cost is spent.
   */
  gateRoleAssign?: boolean;
  className?: string;
}

interface RowProps {
  role: Role;
  checked: boolean;
  busy: boolean;
  disabled: boolean;
  onToggle: (checked: boolean) => void;
  renderTrailing?: (role: Role, denied: boolean) => ReactNode;
}

/** Shortest `policySpecs[].ttlSeconds` — the role's weakest (binding) lifetime. */
function shortestTtl(role: Role): number | undefined {
  const ttls = (role.policySpecs ?? [])
    .map((spec) => spec.ttlSeconds)
    .filter((value): value is number => typeof value === 'number');
  return ttls.length > 0 ? Math.min(...ttls) : undefined;
}

/** The row itself: spinner-or-checkbox, the `name — N policies · TTL 12h` label. */
function RowContent({
  role,
  checked,
  busy,
  disabled,
  denied,
  onToggle,
  renderTrailing,
}: RowProps & { denied: boolean }) {
  const inputId = `role-check-${role._id}`;
  const ttl = formatTtl(shortestTtl(role));
  const inert = disabled || denied;

  return (
    <>
      {busy ? (
        <span
          className="inline-flex h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-muted border-t-foreground motion-reduce:animate-none"
          role="status"
          aria-label="Updating role"
        />
      ) : (
        <Checkbox
          id={inputId}
          checked={checked}
          disabled={inert}
          onCheckedChange={(value) => onToggle(value === true)}
        />
      )}
      <Label
        htmlFor={inputId}
        className={cn(
          'min-w-0 flex-1 font-normal',
          !inert && 'cursor-pointer',
          inert && 'opacity-60',
        )}
      >
        <span className="text-sm text-foreground">{role.name}</span>
        <span className="text-muted-foreground">
          {' — '}
          {formatPolicyCount(role.policySpecs?.length ?? 0)}
          {ttl !== 'TTL —' ? ` · ${ttl}` : ''}
        </span>
      </Label>
      {renderTrailing?.(role, denied)}
    </>
  );
}

/** Ungated row: no probe, no verdict cost. */
function PlainRoleRow(props: RowProps) {
  return (
    <li className="flex items-center gap-3">
      <RowContent {...props} denied={false} />
    </li>
  );
}

/**
 * Gated row — a component of its own because the gate needs a hook per role id.
 * Disabled without a reason while the verdict is in flight (§14.5: no flash of an
 * enabled control), and explained once the engine actually says no.
 */
function GatedRoleRow(props: RowProps) {
  const { allowed } = useCan(PERMISSION_ACTIONS.roleAssign, roleResource(props.role._id));
  const row = <RowContent {...props} denied={allowed !== true} />;

  if (allowed !== false) {
    return <li className="flex items-center gap-3">{row}</li>;
  }

  // A disabled checkbox swallows pointer events, so the tooltip trigger wraps the
  // row in a span that still receives hover.
  return (
    <li className="flex items-center gap-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex min-w-0 flex-1 items-center gap-3">{row}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{ROLE_DENIED_TOOLTIP}</TooltipContent>
      </Tooltip>
    </li>
  );
}

/**
 * Role checklist (design §15.3), reused by the Add member dialog (optional roles)
 * and the Assign roles dialog. Row copy is `name — N policies · TTL 12h`
 * (§10.1/§10.2): the count is `policySpecs.length`, and the TTL the shortest
 * `policySpecs[].ttlSeconds`, which is the lifetime of the role's weakest grant.
 * Loading is four skeleton rows; the empty case explains who to ask (§16.1/§16.2).
 */
export function RoleChecklist({
  roles,
  selectedRoleIds,
  onToggle,
  isLoading = false,
  pendingRoleId = null,
  disabled = false,
  renderTrailing,
  gateRoleAssign = false,
  className,
}: RoleChecklistProps) {
  if (isLoading) {
    return (
      <div className={cn('space-y-3', className)} aria-hidden="true">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="flex items-center gap-3">
            <SkeletonBlock className="h-4 w-4 shrink-0" rounded="sm" />
            <SkeletonBlock className="h-4 flex-1" rounded="sm" />
          </div>
        ))}
      </div>
    );
  }

  if (roles.length === 0) {
    return (
      <EmptyState
        title="No roles available"
        description="Ask an administrator to create roles."
        className={cn('py-8', className)}
      />
    );
  }

  return (
    <ul className={cn('space-y-3', className)}>
      {roles.map((role) => {
        const Row = gateRoleAssign ? GatedRoleRow : PlainRoleRow;
        return (
          <Row
            key={role._id}
            role={role}
            checked={selectedRoleIds.includes(role._id)}
            busy={pendingRoleId === role._id}
            disabled={disabled}
            onToggle={(checked) => onToggle(role._id, checked)}
            renderTrailing={renderTrailing}
          />
        );
      })}
    </ul>
  );
}
